import React, { useContext, useEffect, useState } from "react";
import { APIContext } from "../../../Contexts/APIContext";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import InputLabel from "@mui/material/InputLabel";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { InputProps } from "../Interfaces/InputProps";
import { InputType } from "../Enums/InputType";
import OperationsFiltersViewModel, { AgentNameViewModel } from "./ViewModels/OperationsFiltersViewModel";
import FormControlLabel from "@mui/material/FormControlLabel";
import CallTableViewModel from "../../../ViewModels/CallTableViewModel";
import { handleAutoCompleteChange, handleTextFieldChange } from "../Utility/FilterChangeHandlers";
import Chip from "@mui/material/Chip";

interface IOperationsFiltersDialogProps {
    open: boolean
    setOpen: (value: boolean) => void
    callTableViewModel: CallTableViewModel
    setCallTableViewModel: (value: CallTableViewModel) => void
}

export default function OperationsFiltersDialog({ open, setOpen, callTableViewModel, setCallTableViewModel }: IOperationsFiltersDialogProps) {
    const { get } = useContext(APIContext);
    const [operationsFilters, setOperationsFilters] = useState<OperationsFiltersViewModel>(callTableViewModel.filters.operationsFilters);
    const [sites, setSites] = useState<string[]>([]);
    const [channels, setChannels] = useState<string[]>([]);
    const [teams, setTeams] = useState<string[]>([]);
    const [teamLeaders, setTeamLeaders] = useState<string[]>([]);
    const [agentNames, setAgentNames] = useState<AgentNameViewModel[]>([]);
    const [showInactiveAgentNames, setShowInactiveAgentNames] = useState<boolean>(false);

    useEffect(() => {
        if (open) {
            setOperationsFilters(callTableViewModel.filters.operationsFilters);
        }
    }, [open])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('Search', `GetSiteDropDown?client=${callTableViewModel.filters.client}`, (data) => setSites(data));
            get('Search', `GetChannelsDropDown?client=${callTableViewModel.filters.client}`, (data) => setChannels(data));
            get('Product', `GetAbilityGroupOptions?client=${callTableViewModel.filters.client}`, (data) => setTeams(data));
            get('Search', `GetTeamLeadersDropdown?client=${callTableViewModel.filters.client}`, (data) => setTeamLeaders(data));
            get('Search', `GetAgentDropdown?client=${callTableViewModel.filters.client}&showInactiveAgentNames=${showInactiveAgentNames}`, (data) => setAgentNames(data));
        }
    }, [callTableViewModel.filters.client])

    useEffect(() => {
        if (callTableViewModel.filters.client) {
            get('Search', `GetAgentDropdown?client=${callTableViewModel.filters.client}&showInactiveAgentNames=${showInactiveAgentNames}`, (data) => setAgentNames(data));
        }
    }, [showInactiveAgentNames])

    const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
    const checkedIcon = <CheckBoxIcon fontSize="small" />;

    const inputProps: InputProps[] = [
        {
            name: 'site',
            label: 'Site',
            value: operationsFilters.site,
            options: sites,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'channel',
            label: 'Channel',
            value: operationsFilters.channel,
            options: channels,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'team',
            label: 'Team',
            value: operationsFilters.team,
            options: teams,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'teamLeader',
            label: 'Team Leader',
            value: operationsFilters.teamLeader,
            options: teamLeaders,
            type: InputType.AutoCompleteMultiple
        },
        {
            name: 'agentTypes',
            label: 'Agent Types',
            value: operationsFilters.agentTypes,
            options: ['Live Agents', 'Virtual Agents'],
            type: InputType.AutoCompleteMultiple,
            limitTags: 3
        }
    ];

    const updateAgentName = (newValue: AgentNameViewModel[]) => {
        setOperationsFilters({
            ...operationsFilters,
            agentName: newValue
        });
    }

    const getInput = (inputProps: InputProps) => {
        return (
            <Grid key={inputProps.name} size={12}>
                <InputLabel>{inputProps.label}</InputLabel>
                {
                    inputProps.type === InputType.TextField &&
                    <TextField
                        name={inputProps.name}
                        onChange={(e: any) => handleTextFieldChange(e, setOperationsFilters)}
                        value={inputProps.value}
                        fullWidth
                        autoComplete="off"
                    />
                }
                {
                    inputProps.type === InputType.AutoComplete &&
                    <>
                        <Autocomplete
                            onChange={(_e: any, newValue: string) => handleAutoCompleteChange(inputProps.name, newValue, setOperationsFilters)}
                            options={inputProps.options!}
                            value={inputProps.value as string}
                            fullWidth
                            renderInput={(params) => <TextField {...params} />}
                        />
                    </>
                }
                {
                    inputProps.type === InputType.AutoCompleteMultiple &&
                    <Autocomplete
                        multiple
                        fullWidth
                        options={inputProps.options!}
                        disableCloseOnSelect
                        getOptionLabel={(option) => option}
                        value={inputProps.value as string[]}
                        onChange={(_e: any, newValue: string[]) => { handleAutoCompleteChange(inputProps.name, newValue, setOperationsFilters) }}
                        limitTags={3}
                        renderOption={(props, option, { selected }) => {
                            const { ...optionProps } = props;
                            return (
                                <li {...optionProps}>
                                    <Checkbox
                                        icon={icon}
                                        checkedIcon={checkedIcon}
                                        style={{ marginRight: 8 }}
                                        checked={selected}
                                    />
                                    {option}
                                </li>
                            );
                        }}
                        renderInput={(params) => (
                            <TextField {...params} fullWidth />
                        )}
                        renderTags={(value, getTagProps) => {
                            const numTags = value.length;
                            const limitTags = inputProps.limitTags ?? 1;
                            return (
                                <>
                                    {value.slice(0, limitTags).map((option, index) => (
                                        <Chip
                                            {...getTagProps({ index })}
                                            key={index}
                                            label={option}
                                        />
                                    ))}

                                    {numTags > limitTags && ` +${numTags - limitTags}`}
                                </>
                            );
                        }}
                    />
                }
            </Grid>)
    }

    const confirm = () => {
        const newCallTableViewModel = { ...callTableViewModel };

        newCallTableViewModel.filters.operationsFilters = operationsFilters;

        setCallTableViewModel({
            ...newCallTableViewModel
        })

        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="sm">
            <DialogTitle>Operations Filters</DialogTitle>
            <DialogContent>
                <Grid container height="100%">
                    {
                        inputProps.map(props => getInput(props))
                    }
                    <Grid size={12}>
                        <InputLabel>Agent Name</InputLabel>
                    </Grid>
                    <Grid size={8}>
                        <Autocomplete
                            multiple
                            fullWidth
                            options={agentNames}
                            disableCloseOnSelect
                            getOptionLabel={(option) => option.name}
                            value={operationsFilters.agentName as AgentNameViewModel[]}
                            onChange={(_e: any, newValue: AgentNameViewModel[]) => { updateAgentName(newValue) }}
                            limitTags={3}
                            renderOption={(props, option, { selected }) => {
                                const { ...optionProps } = props;
                                return (
                                    <li {...optionProps}>
                                        <Checkbox
                                            icon={icon}
                                            checkedIcon={checkedIcon}
                                            style={{ marginRight: 8 }}
                                            checked={selected}
                                        />
                                        {option.name}
                                    </li>
                                );
                            }}
                            renderInput={(params) => (
                                <TextField {...params} fullWidth />
                            )}
                            renderTags={(value, getTagProps) => {
                                const numTags = value.length;
                                const limitTags = 1;
                                return (
                                    <>
                                        {value.slice(0, limitTags).map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={index}
                                                label={option.name}
                                            />
                                        ))}

                                        {numTags > limitTags && ` +${numTags - limitTags}`}
                                    </>
                                );
                            }}
                        />
                    </Grid>
                    <Grid size={4} justifyContent='center'>
                        <FormControlLabel sx={{ margin: 0 }}
                            control={
                                <Checkbox
                                    checked={showInactiveAgentNames}
                                    onChange={() => setShowInactiveAgentNames(!showInactiveAgentNames)}
                                />}
                            label="Show inactive agents"
                        />
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => setOpen(false)}>
                    Cancel
                </Button>
                <Button onClick={confirm}>
                    Confirm
                </Button>
            </DialogActions>
        </Dialog>
    )
}